// オンライン申込フォーム(user)のバリデーション
import {
  validateBankName as validateBankNameBase,
  validateBranchName as validateBranchNameBase,
  validateAccountType as validateAccountTypeBase,
  validateAccountHolderName as validateAccountHolderNameBase,
  validateAccountNumber as validateAccountNumberBase
} from '@this/components/admin/online_applications/shared/bank_account_validation';
import {
  validateStringField,
  validateEmail as validateEmailBase
} from '@this/components/admin/online_applications/shared/common_validation';
import {
  validateContactName as validateContactNameBase,
  validateContactNameKana as validateContactNameKanaBase,
  validateContactEmail as validateContactEmailBase,
  validateContactTel as validateContactTelBase
} from '@this/components/admin/online_applications/shared/contact_validation';
import type { ValidationErrors } from './types';

export const validateContactNameKana = (value: string): string | undefined => {
  return validateContactNameKanaBase(value, true);
};

export const validateContactName = (value: string): string | undefined => {
  return validateContactNameBase(value, true);
};

export const validateContactEmail = (value: string): string | undefined => {
  return validateContactEmailBase(value, true);
};

export const validateContactTel = (value: string): string | undefined => {
  return validateContactTelBase(value, true);
};

export const validateSubmitterCompanyName = (value: string): string | undefined => {
  return validateStringField(value, '会社名', { required: true });
};

export const validateSubmitterDepartment = (value: string): string | undefined => {
  return validateStringField(value, '所属部署名', { required: true });
};

export const validateSubmitterName = (value: string): string | undefined => {
  return validateStringField(value, '氏名', { required: true });
};

export const validateSubmitterEmail = (value: string): string | undefined => {
  return validateEmailBase(value, 'メールアドレス', true);
};

// 返金口座情報のバリデーション（User側は必須）
export const validateBankName = (value: string): string | undefined => {
  return validateBankNameBase(value, true);
};

export const validateBranchName = (value: string): string | undefined => {
  return validateBranchNameBase(value, true);
};

export const validateAccountType = (value: string): string | undefined => {
  return validateAccountTypeBase(value, true);
};

export const validateAccountHolderName = (value: string): string | undefined => {
  return validateAccountHolderNameBase(value, true);
};

export const validateAccountNumber = (value: string): string | undefined => {
  return validateAccountNumberBase(value, true);
};

export type ValidationFieldName =
  | 'contactNameKana'
  | 'contactName'
  | 'contactEmail'
  | 'contactTel'
  | 'submitterCompanyName'
  | 'submitterDepartment'
  | 'submitterName'
  | 'submitterEmail'
  | 'bankName'
  | 'branchName'
  | 'accountType'
  | 'accountHolderName'
  | 'accountNumber';

const validationMap: Record<ValidationFieldName, (value: string) => string | undefined> = {
  contactNameKana: validateContactNameKana,
  contactName: validateContactName,
  contactEmail: validateContactEmail,
  contactTel: validateContactTel,
  submitterCompanyName: validateSubmitterCompanyName,
  submitterDepartment: validateSubmitterDepartment,
  submitterName: validateSubmitterName,
  submitterEmail: validateSubmitterEmail,
  bankName: validateBankName,
  branchName: validateBranchName,
  accountType: validateAccountType,
  accountHolderName: validateAccountHolderName,
  accountNumber: validateAccountNumber
};

/**
 * バリデーション
 * @param field バリデーション対象のフィールド名
 * @param value バリデーション対象の値
 * @returns エラーメッセージ（エラーがない場合はundefined）
 */
export const validateField = (field: ValidationFieldName, value: string): string | undefined => {
  return validationMap[field]?.(value);
};

export const validateAllFields = (values: {
  contactNameKana: string;
  contactName: string;
  contactEmail: string;
  contactTel: string;
  submitterCompanyName?: string;
  submitterDepartment?: string;
  submitterName?: string;
  submitterEmail?: string;
  bankName?: string;
  branchName?: string;
  accountType?: string;
  accountHolderName?: string;
  accountNumber?: string;
}): ValidationErrors => {
  const errors: ValidationErrors = {};

  const nameKanaError = validateContactNameKana(values.contactNameKana);
  if (nameKanaError) errors.contactNameKana = nameKanaError;

  const nameError = validateContactName(values.contactName);
  if (nameError) errors.contactName = nameError;

  const emailError = validateContactEmail(values.contactEmail);
  if (emailError) errors.contactEmail = emailError;

  const telError = validateContactTel(values.contactTel);
  if (telError) errors.contactTel = telError;

  if (values.submitterCompanyName !== undefined) {
    const submitterCompanyNameError = validateSubmitterCompanyName(values.submitterCompanyName);
    if (submitterCompanyNameError) errors.submitterCompanyName = submitterCompanyNameError;
  }

  if (values.submitterDepartment !== undefined) {
    const submitterDepartmentError = validateSubmitterDepartment(values.submitterDepartment);
    if (submitterDepartmentError) errors.submitterDepartment = submitterDepartmentError;
  }

  if (values.submitterName !== undefined) {
    const submitterNameError = validateSubmitterName(values.submitterName);
    if (submitterNameError) errors.submitterName = submitterNameError;
  }

  if (values.submitterEmail !== undefined) {
    const submitterEmailError = validateSubmitterEmail(values.submitterEmail);
    if (submitterEmailError) errors.submitterEmail = submitterEmailError;
  }

  if (values.bankName !== undefined) {
    const bankNameError = validateBankName(values.bankName);
    if (bankNameError) errors.bankName = bankNameError;
  }

  if (values.branchName !== undefined) {
    const branchNameError = validateBranchName(values.branchName);
    if (branchNameError) errors.branchName = branchNameError;
  }

  if (values.accountType !== undefined) {
    const accountTypeError = validateAccountType(values.accountType);
    if (accountTypeError) errors.accountType = accountTypeError;
  }

  if (values.accountHolderName !== undefined) {
    const accountHolderNameError = validateAccountHolderName(values.accountHolderName);
    if (accountHolderNameError) errors.accountHolderName = accountHolderNameError;
  }

  if (values.accountNumber !== undefined) {
    const accountNumberError = validateAccountNumber(values.accountNumber);
    if (accountNumberError) errors.accountNumber = accountNumberError;
  }

  return errors;
};

export const hasErrors = (errors: ValidationErrors): boolean => {
  return Object.keys(errors).length > 0;
};
